import React from 'react';
import { useTranslation } from 'react-i18next';
import { Gamepad2, Zap } from 'lucide-react';

interface VPSLocationSelectorProps {
  selectedLocation: 'game' | 'performance';
  onLocationChange: (location: 'game' | 'performance') => void;
}

export function VPSLocationSelector({ selectedLocation, onLocationChange }: VPSLocationSelectorProps) {
  const { t } = useTranslation();

  const locations = [
    {
      id: 'game',
      icon: Gamepad2,
      title: t('vps.locations.germanyGame'),
      description: t('vps.locations.gameDescription'),
      gradient: 'from-blue-600 to-blue-700'
    },
    {
      id: 'performance',
      icon: Zap,
      title: t('vps.locations.germanyPerf'),
      description: t('vps.locations.perfDescription'),
      gradient: 'from-purple-600 to-purple-700'
    }
  ];

  return (
    <div className="flex flex-col items-center gap-8 mb-12">
      <div className="flex flex-wrap justify-center gap-6">
        {locations.map((location) => {
          const Icon = location.icon;
          const isSelected = selectedLocation === (location.id as 'game' | 'performance');

          return (
            <button
              key={location.id}
              onClick={() => onLocationChange(location.id as 'game' | 'performance')}
              className={`relative group p-6 rounded-xl transition-all duration-300 w-full md:w-[calc(50%-1.5rem)] ${
                isSelected
                  ? `bg-gradient-to-r ${location.gradient} shadow-lg scale-105`
                  : 'bg-white shadow-md hover:shadow-lg hover:scale-105'
              }`}
            >
              <div className="flex items-center gap-4">
                <div
                  className={`p-3 rounded-lg ${
                    isSelected ? 'bg-white/10' : 'bg-gray-100 group-hover:bg-gray-200'
                  } transition-colors`}
                >
                  <Icon
                    className={`w-8 h-8 ${
                      isSelected
                        ? 'text-white'
                        : location.id === 'performance'
                        ? 'text-purple-600'
                        : 'text-blue-600'
                    }`}
                  />
                </div>
                <div>
                  <span
                    className={`font-bold text-xl block ${
                      isSelected ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {location.title}
                  </span>
                    <span
                      className={`text-sm block mt-1 whitespace-pre-line ${
                        isSelected ? 'text-blue-100' : 'text-gray-500'
                      }`}
                    >
                      {location.description}
                    </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
